import React from 'react';
import { PageView } from '../types';
import { MuseForgePlayground } from '../components/MuseForgePlayground';
import { ConfigDiffViewer } from '../components/ConfigDiffViewer';
import { ChevronRight } from 'lucide-react';

interface FeaturesPageProps {
  setActivePage: (page: PageView) => void;
  onOpenLeadModal: () => void;
}

export const FeaturesPage: React.FC<FeaturesPageProps> = ({ onOpenLeadModal }) => {
  return (
    <div className="features-minimal">
      {/* HERO HEADER */}
      <section className="f-hero-center">
        <div className="section-container">
          <h1 className="f-title">Engineered for NOC Leaders, Architects &amp; Engineers</h1>
          <p className="f-sub">
            An in-depth look at how MUSE Telemetry ingests, normalizes, correlates, and audits telemetry across complex, mixed-vendor telecom and ISP networks.
          </p>
        </div>
      </section>

      {/* 1. HARDWARE INGESTION: SPACIOUS 2-COLUMN GRID */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="grid-2">
            <div>
              <div className="card-header-dot">
                <span className="status-dot blue" />
                <span className="card-tag">INGESTION ENGINE</span>
              </div>
              <h2 className="f-h2">Hardware-Agnostic Telemetry Normalization</h2>
              <p className="f-desc">
                Traditional NMS software relies on vendor-specific MIBs or gNMI interfaces exclusively. MUSE Telemetry bridges legacy infrastructure with modern cloud architectures by supporting the complete spectrum of network collection interfaces.
              </p>

              <div className="proto-stack">
                <div className="p-row">
                  <span className="p-title">SNMP (v1, v2c, v3)</span>
                  <span className="p-sub">High-density polling &amp; trap processing.</span>
                </div>
                <div className="p-row">
                  <span className="p-title">REST &amp; gRPC APIs</span>
                  <span className="p-sub">Programmatic collection for software-defined OLTs.</span>
                </div>
                <div className="p-row">
                  <span className="p-title">SSH / Telnet CLI Extraction</span>
                  <span className="p-sub">Structured regex parsing for legacy gear lacking APIs.</span>
                </div>
                <div className="p-row">
                  <span className="p-title">Streaming Telemetry (gNMI / IPFIX)</span>
                  <span className="p-sub">Sub-second metric streaming for transport layers.</span>
                </div>
              </div>
            </div>

            {/* Ingestion Visual Card Aligned to Bottom of Column */}
            <div className="ingest-frame-column">
              <div className="flat-card ingest-frame">
                <div className="frame-top">
                  <span className="status-dot green" />
                  <span className="frame-title">UNIFIED SCHEMATIC DATA MAPPING</span>
                </div>
                <div className="frame-body">
                  <div className="ingest-node">Cisco IOS-XE (CLI)</div>
                  <div className="ingest-node">Juniper JunOS (SNMP)</div>
                  <div className="ingest-node">MikroTik (RouterOS API)</div>
                  <div className="ingest-arrow">↓ Data Normalization Engine</div>
                  <div className="ingest-output">
                    <code>{`{ "device": "optical_switch", "rx_power_dbm": -18.4, "status": "alert" }`}</code>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. MUSE FORGE PARSER PLAYGROUND */}
      <section className="section-padding border-top">
        <div className="section-container">
          <MuseForgePlayground />
        </div>
      </section>

      {/* 3. EVENT CLUSTERING & DUAL PERSPECTIVE: 2-COLUMN GRID */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="grid-2">
            <div className="flat-card">
              <div className="card-header-dot">
                <span className="status-dot red" />
                <span className="card-tag">CORRELATION LAYER</span>
              </div>
              <h3>Event Clustering &amp; Fiber-Cut Isolation</h3>
              <p>
                When a major link fails, downstream devices lose connectivity simultaneously. MUSE Telemetry's <strong>Dependency-Aware Clustering Engine</strong> evaluates physical topology and time-series correlation to isolate the root point of failure, suppressing 500 cascading alarms.
              </p>
            </div>

            <div className="flat-card">
              <div className="card-header-dot">
                <span className="status-dot green" />
                <span className="card-tag">DUAL-PERSPECTIVE</span>
              </div>
              <h3>Outside-In &amp; Inside-Out Visibility</h3>
              <p>
                Bridge the gap between green NOC dashboards and complaining customers. Compare internal switch fabric and routing table health against real-world customer-edge reachability, DNS latency, and BGP path consistency.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. CONFIG AUDITING VIEWER */}
      <section className="section-padding border-top">
        <div className="section-container">
          <ConfigDiffViewer />
        </div>
      </section>

      {/* 5. PROTOCOL & CAPABILITY MATRIX */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="section-heading">
            <h2>Supported Protocols &amp; Data Pipeline Matrix</h2>
            <p>Comprehensive breakdown of ingestion protocols, parsing engines, and export targets.</p>
          </div>

          <div className="flat-card table-frame">
            <table className="matrix-table">
              <thead>
                <tr>
                  <th>Protocol / Interface</th>
                  <th>Polling / Stream Rate</th>
                  <th>Normalization Engine</th>
                  <th>Export Formats</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>SNMP (v1, v2c, v3)</td>
                  <td>10s - 60s configurable</td>
                  <td>MUSE MIB Auto-Parser</td>
                  <td>Prometheus, OpenTelemetry, JSON</td>
                </tr>
                <tr>
                  <td>SSH / Telnet CLI</td>
                  <td>5m snapshot / On-demand</td>
                  <td>MUSE Forge Regex Engine</td>
                  <td>Structured JSON, Git Diff</td>
                </tr>
                <tr>
                  <td>gNMI / OpenConfig</td>
                  <td>Sub-second streaming</td>
                  <td>Native Protobuf Decoder</td>
                  <td>ClickHouse, TimescaleDB, Influx</td>
                </tr>
                <tr>
                  <td>REST &amp; gRPC APIs</td>
                  <td>Event-driven webhooks</td>
                  <td>JSON Schema Normalizer</td>
                  <td>Kafka, Webhooks, WebSockets</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CALL TO ACTION */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="flat-card cta-card-center">
            <h2>Ready to Unify Your Telemetry Stack?</h2>
            <p className="cta-lead">
              Schedule a 1-on-1 technical deep dive with our lead infrastructure engineers.
            </p>
            <div className="cta-actions">
              <button onClick={onOpenLeadModal} className="btn btn-primary">
                <span>Schedule a Technical Deep Dive</span>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .f-hero-center {
          padding: 6rem 0 4rem;
          text-align: center;
          max-width: 840px;
          margin: 0 auto;
        }

        .f-title {
          font-size: 2.8rem;
          font-weight: 800;
          color: #ffffff;
          letter-spacing: -0.03em;
          margin-bottom: 1rem;
        }

        .f-sub {
          font-size: 1.1rem;
          color: var(--text-muted);
          line-height: 1.6;
        }

        .f-h2 {
          font-size: 1.8rem;
          font-weight: 700;
          margin-bottom: 0.75rem;
        }

        .f-desc {
          font-size: 1rem;
          color: var(--text-muted);
          line-height: 1.6;
          margin-bottom: 2rem;
        }

        .proto-stack {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .p-row {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          padding: 0.85rem 1.1rem;
          border-radius: var(--radius-sm);
        }

        .p-title {
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: #ffffff;
        }

        .p-sub {
          font-size: 0.78rem;
          color: var(--text-muted);
        }

        /* Ingestion Frame Column Alignment */
        .ingest-frame-column {
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          height: 100%;
        }

        .ingest-frame {
          padding: 0;
          overflow: hidden;
          height: auto;
          width: 100%;
        }

        .frame-top {
          background: #0d0d0d;
          border-bottom: 1px solid var(--border-color);
          padding: 0.6rem 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .frame-title {
          font-family: var(--font-mono);
          font-size: 0.75rem;
          color: var(--text-muted);
        }

        .frame-body {
          padding: 1.25rem;
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
        }

        .ingest-node {
          background: #000000;
          border: 1px solid var(--border-color);
          padding: 0.6rem;
          border-radius: 4px;
          font-family: var(--font-mono);
          font-size: 0.78rem;
          color: #ffffff;
        }

        .ingest-arrow {
          font-family: var(--font-mono);
          font-size: 0.75rem;
          color: var(--text-dim);
          text-align: center;
        }

        .ingest-output {
          background: #000000;
          border: 1px solid var(--border-color);
          padding: 0.75rem;
          border-radius: 4px;
          font-family: var(--font-mono);
          font-size: 0.75rem;
          color: #10b981;
        }

        /* Matrix Table */
        .table-frame {
          padding: 0;
          overflow-x: auto;
        }

        .matrix-table {
          width: 100%;
          border-collapse: collapse;
          text-align: left;
          font-size: 0.88rem;
        }

        .matrix-table th, .matrix-table td {
          padding: 0.85rem 1.25rem;
          border-bottom: 1px solid var(--border-color);
        }

        .matrix-table th {
          background: #0d0d0d;
          color: #ffffff;
          font-weight: 600;
        }

        .matrix-table td {
          color: var(--text-muted);
        }



        @media (max-width: 800px) {
          .f-title { font-size: 2rem; }
          .ingest-frame-column { justify-content: flex-start; }
        }
      `}</style>
    </div>
  );
};
